import { useState, useEffect } from 'react'
import DataTable, { Column } from '@/components/DataTable'
import StatusBadge from '@/components/StatusBadge'
import PipelineFunnelChart from '@/components/PipelineFunnelChart'
import { DollarSign, TrendingUp, Target, Layers } from 'lucide-react'

interface Deal {
  id: number
  name: string
  client_name: string
  value: number
  stage: string
  probability: number
  expected_close_date: string
  deal_type: string
  currency: string
}

interface PipelineStage {
  stage: string
  count: number
  total_value: number
  weighted_value: number
  avg_probability: number
}

interface ForecastData {
  stages: PipelineStage[]
  metrics: {
    total_pipeline_value: number
    total_weighted_value: number
    total_deals: number
    overall_probability: number
  }
  conversion_rates: Array<{ stage: string; rate: number }>
}

const mockDeals: Deal[] = [
  {
    id: 1,
    name: 'TechVenture UAE Market Entry',
    client_name: 'TechVenture AI Ltd',
    value: 50000,
    stage: 'CSA Negotiation',
    probability: 80,
    expected_close_date: '2025-12-10',
    deal_type: 'Advisory',
    currency: 'USD',
  },
  {
    id: 2,
    name: 'HealthTech Saudi Expansion',
    client_name: 'HealthTech Solutions',
    value: 50000,
    stage: 'Engagement Delivered',
    probability: 90,
    expected_close_date: '2025-12-05',
    deal_type: 'Advisory',
    currency: 'USD',
  },
  {
    id: 3,
    name: 'Fintech GCC Advisory',
    client_name: 'Fintech Innovations',
    value: 50000,
    stage: 'Proposal Shared',
    probability: 60,
    expected_close_date: '2025-12-20',
    deal_type: 'Advisory',
    currency: 'USD',
  },
  {
    id: 4,
    name: 'CleanEnergy Corporate Development',
    client_name: 'CleanEnergy Partners',
    value: 50000,
    stage: 'CSA Negotiation',
    probability: 75,
    expected_close_date: '2025-12-15',
    deal_type: 'Advisory',
    currency: 'USD',
  },
  {
    id: 5,
    name: 'Advanced Mfg Business Dev',
    client_name: 'Advanced Manufacturing Co',
    value: 50000,
    stage: 'Qualified Lead',
    probability: 50,
    expected_close_date: '2025-12-31',
    deal_type: 'Advisory',
    currency: 'USD',
  },
]

export default function PipelinePage() {
  const [deals, setDeals] = useState<Deal[]>(mockDeals)
  const [forecast, setForecast] = useState<ForecastData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedStage, setSelectedStage] = useState<string | null>(null)

  useEffect(() => {
    fetchPipelineData()
  }, [])

  const fetchPipelineData = async () => {
    try {
      // Fetch deals
      const dealsResponse = await fetch('/api/deals')
      const dealsData = await dealsResponse.json()
      if (dealsData.success) {
        setDeals(dealsData.data)
      }

      // Fetch forecast
      const forecastResponse = await fetch('/api/pipeline/forecast')
      const forecastData = await forecastResponse.json()
      if (forecastData.success) {
        setForecast(forecastData.data)
      }
    } catch (error) {
      console.error('Error fetching pipeline data:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredDeals = selectedStage
    ? deals.filter(d => d.stage === selectedStage)
    : deals

  const columns: Column<Deal>[] = [
    {
      key: 'name',
      label: 'Deal Name',
      sortable: true,
    },
    {
      key: 'client_name',
      label: 'Client',
      sortable: true,
    },
    {
      key: 'value',
      label: 'Value',
      sortable: true,
      render: (deal) => `$${(deal.value / 1000).toFixed(0)}K`,
    },
    {
      key: 'stage',
      label: 'Stage',
      render: (deal) => {
        const statusMap: Record<string, 'info' | 'warning' | 'success'> = {
          'Qualified Lead': 'info',
          'Proposal Shared': 'info',
          'CSA Negotiation': 'warning',
          'Engagement Delivered': 'success',
        }
        return <StatusBadge status={statusMap[deal.stage] || 'info'} label={deal.stage} />
      },
    },
    {
      key: 'probability',
      label: 'Probability',
      sortable: true,
      render: (deal) => `${deal.probability}%`,
    },
    {
      key: 'expected_close_date',
      label: 'Expected Close',
      sortable: true,
      render: (deal) => deal.expected_close_date ? new Date(deal.expected_close_date).toLocaleDateString() : 'N/A',
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-royal-blue"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-dashboard-title text-royal-blue-700 mb-2">Deal Pipeline</h1>
        <p className="text-graphite-grey-500">
          Track and manage active deals through the advisory process
        </p>
      </div>

      {/* Pipeline Metrics */}
      {forecast && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-royal-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-royal-blue-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Total Pipeline</p>
            <p className="text-3xl font-bold text-royal-blue">
              ${(forecast.metrics.total_pipeline_value / 1000).toFixed(0)}K
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-golden-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-golden-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Weighted Value</p>
            <p className="text-3xl font-bold text-golden">
              ${(forecast.metrics.total_weighted_value / 1000).toFixed(0)}K
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Avg Probability</p>
            <p className="text-3xl font-bold text-green-600">
              {forecast.metrics.overall_probability}%
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Layers className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Active Deals</p>
            <p className="text-3xl font-bold text-purple-600">
              {forecast.metrics.total_deals}
            </p>
          </div>
        </div>
      )}

      {/* Pipeline Funnel Chart */}
      {forecast && forecast.stages.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <PipelineFunnelChart stages={forecast.stages} />
        </div>
      )}

      {/* Stage Filter */}
      {forecast && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-graphite-grey">Filter by stage:</span>
          <button
            onClick={() => setSelectedStage(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedStage === null
                ? 'bg-royal-blue text-white'
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            All ({deals.length})
          </button>
          {forecast.stages.map(stage => (
            <button
              key={stage.stage}
              onClick={() => setSelectedStage(stage.stage)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStage === stage.stage
                  ? 'bg-royal-blue text-white'
                  : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
              }`}
            >
              {stage.stage} ({stage.count})
            </button>
          ))}
        </div>
      )}

      {/* Deals Table */}
      <DataTable
        columns={columns}
        data={filteredDeals}
        keyExtractor={(deal) => deal.id}
        emptyMessage="No deals in pipeline"
      />
    </div>
  )
}
